import React from 'react';

/**
 * Shared AI action button. Active when the user is Pro and has uses remaining.
 * Greyed with tooltip for Free users or when the weekly limit is reached.
 *
 * @param {Object} props
 * @param {string}   props.label          - Button label text
 * @param {boolean}  props.isPro          - Whether the user has a Pro subscription
 * @param {number}   props.usageRemaining - Calls remaining this week (0 = at limit)
 * @param {string}   props.freeTooltip    - Tooltip shown when user is Free or at limit
 * @param {Function} props.onClick        - Click handler (only called when active)
 * @param {boolean}  [props.loading]      - Show spinner and disable while loading
 * @param {boolean}  [props.disabled]     - Extra disabled condition (e.g. no players selected)
 * @param {string}   [props.disabledTooltip] - Tooltip shown when `disabled` is true, taking
 *                   priority over the isPro/usageRemaining tooltips (e.g. "unavailable today")
 */
export default function AiButton({ label, isPro, usageRemaining, freeTooltip, onClick, loading, disabled, disabledTooltip }) {
  const isDisabled = !isPro || usageRemaining <= 0 || loading || !!disabled;

  const tooltipText = loading
    ? null
    : disabled && disabledTooltip
    ? disabledTooltip
    : !isPro
    ? freeTooltip
    : usageRemaining <= 0
    ? `Weekly limit reached (${usageRemaining === 0 ? 'limit' : usageRemaining}/limit). Resets next week.`
    : null;

  return (
    <div className="ai-button-wrapper">
      <button
        className={`ai-button${isDisabled ? ' ai-button--disabled' : ''}`}
        onClick={isDisabled ? undefined : onClick}
        disabled={isDisabled}
        title={tooltipText || undefined}
        aria-label={label}
      >
        {loading ? (
          <>
            <span className="ai-button-spinner" aria-label="Loading" />
            Diagnosing…
          </>
        ) : (
          <>✦ {label}</>
        )}
      </button>
      {tooltipText && (
        <span className="ai-button-tooltip" role="tooltip">
          {tooltipText}
        </span>
      )}
      {isPro && !loading && usageRemaining > 0 && (
        <span className="ai-usage-counter">{usageRemaining} use{usageRemaining !== 1 ? 's' : ''} remaining this week</span>
      )}
    </div>
  );
}
